import {
  ProviderConsumer as FluentUIThemeConsumer,
  Input,
  Text
} from '@fluentui/react-northstar';
import { themeNames } from '@fluentui/react-teams';
import { PersonType, UserType } from '@microsoft/mgt-components';
import { PeoplePicker } from '@microsoft/mgt-react';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { appState, AppStateContext } from '../../../AppState';
import { CustomTimePicker } from '../../../custom-properties/CustomTimePicker';
import { CustomPropertyDataType_LABELS } from '../../../custom-properties/utils/constants';
import { getStatusIdByGuid } from '../../../tikit/ticketHelper';
import { getCachedFeature } from '../../cache/FeatureCache';
import { searchUsers } from '../../cache/PlatformUserCache';
import { TicketDatePicker } from '../../common/Controls/TicketDateTimePicker';
import { ThemeColorScheme } from '../../common/TeamsTheme';
import { ICustomProperty } from '../../interfaces/customproperties.interface';
import { ISupportGroup, ITeam } from '../../interfaces/supportGroup.interface';
import { LICENSE_FEATURE, STATUS_GUID, TAGS } from '../../utils/constants';
import { CheckFeature } from '../CheckFeature';
import { LabelWithCheckbox } from '../LabelWithCheckbox/LabelWithCheckbox';
import { Item } from '../NestedDropdown/NestedDropdown';
import { TypeaheadPicker } from '../NestedDropdown/TypeaheadPicker';
import { TypeaheadItemPicker } from '../NestedDropdown/TypeaheadItemPicker';
import { MultiTypeaheadField } from '../table/TypeaheadField';
import { useCreateTag } from '../table/useCreateTag';
import {
  getTagsList,
  LabelElement
} from '../TicketForm';
import { getCardTimeInputISOFormatted } from '../TicketTemplateAnswer';
import { FieldErrorText } from '../FieldErrorText';
import { bulkEditEmptyFields, bulkEditFilledFields, customPropertyKey } from './bulkEditRequired';
import Styles from './BulkEditTicket.module.css';
import { sameTeamsTeam, teamHasGroups } from '../../utils/teamGroupRule';

interface Props {
  ticketData: BulkTicketEntity;
  setTicketData: (
    ticketData:
      | BulkTicketEntity
      | ((value: BulkTicketEntity) => BulkTicketEntity)
  ) => void;
}

/** A True / False property's stored value as its option ("False" 0, "True" 1), and back. */
const boolOptionId = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  return value === "true" ? 1 : 0;
};
const boolOptionValue = (id: number) => (id > 0 ? "true" : "false");

export const BulkEditTicketForm =({ ticketData, setTicketData }: Props) => {
  const {
    ticketStatus,
    categories,
    priority,
    ticketTypes,
    supportgroups,
    usersInRole,
    teams
  } = useContext(AppStateContext);

  const [supportGroupsBulkEdit, setSupportGroupsBulkEdit] = useState<ISupportGroup[]>([]);
  const [teamsBulkEdit, setTeamsBulkEdit] = useState<ITeam[]>([]);
  const [tagList, setTagList] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState(usersInRole);
  const [filteredGroups, setFilteredGroups] = useState(supportgroups);
  const [enabledMultiDepartment, setEnabledMultiDepartment] = useState(false);

  const currentState = appState();
  const defaultAssigneeOption = { Id: -1, FullName: 'Unassigned' };
  const { t } = useTranslation();


  (async () => {
    const enabled = await getCachedFeature(LICENSE_FEATURE.MultiDepartment);
    setEnabledMultiDepartment(enabled);
  })();

  useEffect(() => {
    let _group = [
      ...filteredGroups
    ];

    // A team with groups needs one, so it is not offered "No group" (#33693).
    const teamRequiresGroup = teamHasGroups(teams.find(x => x.Id == ticketData.TeamId?.value), supportgroups);
    if (teamRequiresGroup)
      _group = _group.filter(x => x.Id != -1);
    else if(_group.map(x => x.Id).indexOf(-1) == -1)
      _group.unshift(
        {
          Id: -1,
          Members: [],
          Name: t('ticket.ticket-list.bulk-edit.no-group'),
          Description: '',
          TeamsChannelId: '',
          TeamsAadObjectId: '',
          ApplicationRoleId: 0
        } as ISupportGroup);

    setSupportGroupsBulkEdit(_group);
  }, [filteredGroups, ticketData.TeamId?.value]);

  useEffect(() => {
    const _team = [...teams];
    setTeamsBulkEdit(_team);
  }, [teams]);

  useEffect(() => {
    const tagsPromises = getTagsList();
    tagsPromises.then(taglist => {
      setTagList(taglist);
    });
  }, []);

  /** A list's items as the typable pickers take them: name and id. */
  const pickerItems = (list: any[], header: string) => (list ?? []).map(item => ({ header: item[header], accessibilityitemprops: item.Id }));
  /** Status, Type, Category, Priority as TypeaheadPicker takes them. */
  const enumItems = (list: any[]): Item[] => (list ?? []).map(item => ({ id: item.Id, parentId: item.ParentId, position: item.Position, value: item.Value, ...item }));

  /** The Tags box: the tags offered, in name order. */
  const tagOptions = useMemo(() => tagList
    .map((tag: any) => ({ id: tag.accessibilityitemprops as number, name: tag.header as string }))
    .sort((a, b) => a.name.localeCompare(b.name)), [tagList]);

  /** "New tag 'name'" in the Tags box: the new tag is picked (the box adds it). */
  const createTag = useCreateTag();
  const createTagFromBox = async (name: string): Promise<{ id: number; name: string } | null> => {
    const tag = await createTag(name);
    if (!tag) return null;
    setTagList(prev => prev.some((other: any) => other.accessibilityitemprops === tag.Id)
      ? prev
      : [...prev, { header: tag.Name, accessibilityitemprops: tag.Id, selected: false }]);
    return { id: tag.Id, name: tag.Name };
  };

  /** The Collaborators box's people - not the assignee being set, as before. */
  const collaboratorOptions = useMemo(() => usersInRole
    .filter(user => ticketData.AssigneeId.value != user.Id)
    .map(user => ({ id: user.Id, name: user.FullName })), [usersInRole, ticketData.AssigneeId.value]);

  const requestChangeHandler = async (e: any) => {
    // Removing the person empties the field - it used to keep the last one, so Update still sent them.
    if (e.detail.length === 0) {
      setTicketData(preState => ({ ...preState, RequesterId: { ...preState.RequesterId, value: null } }));
      return;
    }

    const { displayName, givenName, surname, userPrincipalName, id, scoredEmailAddresses } = e.detail[0];
    const payload = {
      FullName: displayName,
      GivenName: givenName,
      FamilyName: surname,
      Email: scoredEmailAddresses?.[0]?.address,
      UserName: userPrincipalName,
      AadObjectId: id
    };

    setTicketData(preState => ({
      ...preState,
      RequesterId: {
        ...preState.RequesterId,
        value: payload
      }
    }));
  };

  const affectedUserChangeHandler = async (e: any) => {
    const userList = [];
    if (e.detail.length > 0) {
      let users = e.detail.map((item: any) => {
        const { id, displayName, givenName, surname, userPrincipalName, scoredEmailAddresses } = item;
        return {
          Name: displayName,
          GivenName: givenName,
          FamilyName: surname,
          Email: scoredEmailAddresses?.[0]?.address,
          UserName: userPrincipalName ?? "",
          AadObjectId: id ?? null,
        };
      });
      
      for (let u of users) 
        userList.push((await searchUsers(u)).Id);
    }

    setTicketData(preState => ({
      ...preState,
      AffectedUsers: {
        ...preState.AffectedUsers,
        value: userList
      }
    }));
  };
  const collaboratorChangeHandler = (userList: number[]) => {
    setTicketData(preState => ({
      ...preState,
      TicketCollaborators: {
        ...preState.TicketCollaborators,
        value: userList
      }
    }));
  };

  const assignmentCheckbox = (multiDepartmentEnabled, dataTicket) => {
    return multiDepartmentEnabled ? dataTicket.TeamId.required : dataTicket.SupportGroupId.required
  }

  /**
   * Ticked fields that have held a value since they were ticked. A ticked field with no value holds Update back and is
   * marked required, but says so under itself only once it has had a value and been emptied (Shaun, 2026-10-20);
   * unticking forgets it.
   */
  const [filledOnce, setFilledOnce] = useState<string[]>([]);
  const emptyFields = bulkEditEmptyFields(ticketData);
  useEffect(() => {
    const ticked = [...bulkEditFilledFields(ticketData), ...emptyFields];
    const filled = bulkEditFilledFields(ticketData);
    setFilledOnce(prev => {
      const next = [...prev.filter(key => ticked.includes(key)), ...filled.filter(key => !prev.includes(key))];
      return next.length === prev.length && next.every(key => prev.includes(key)) ? prev : next;
    });
  }, [ticketData]);
  /** A new Team empties Group and Assignee, a new Group empties Assignee - the form did that, not the agent. */
  const forgetFilled = (keys: string[]) => setFilledOnce(prev => prev.filter(key => !keys.includes(key)));
  const emptyMessage = (key: string): string | undefined =>
    emptyFields.includes(key) && filledOnce.includes(key) ? t('ticket-details.edit-ticket-view.required-field-must-be-filled-out') : undefined;

  const ifTeamNotSelected = (value) => {
    defaultAssigneeOption.FullName = t('ticket.ticket-board.ticket-card.unassigned');
    if (value.Id === -1 || value.Id == null) {
      setFilteredGroups([...supportgroups]);
      setFilteredUsers([ { ...defaultAssigneeOption }, ...usersInRole ]);
    }
    else {
      setFilteredGroups([...supportgroups.filter(x => sameTeamsTeam(x, value))]);
      setFilteredUsers([ { ...defaultAssigneeOption }, ...(currentState.teams.filter(x => x.Id == value.Id)[0]?.Users || []) ]);
    }
  }

  const filterGroups = (value: any) => {
    defaultAssigneeOption.FullName = t('ticket.ticket-board.ticket-card.unassigned');
    if (value?.Id && value.Id != -1 && !value.TeamsAadObjectId) {
      setFilteredGroups([...supportGroupsBulkEdit.filter(x => x.Id == -1)]);
      setFilteredUsers([ { ...defaultAssigneeOption }, ...[] ]);
    }
    else {
      ifTeamNotSelected(value);
    }
  }

  const handlerTeam = (value: any, event: string) => {
    setTicketData(preState => ({ ...preState,
      TeamId: { ...preState.TeamId, value: value.Id },
      SupportGroupId: { ...preState.SupportGroupId, value: null },
      AssigneeId: { ...preState.AssigneeId, required: true, value: null }
    }));

    if (event == 'onChange') {
      filterGroups(value);
      forgetFilled(['SupportGroupId', 'AssigneeId']);
    }
  }

  const ifGroupNotSelected = (value: any) => {
    defaultAssigneeOption.FullName = t('ticket.ticket-board.ticket-card.unassigned');
    if (!ticketData.TeamId.value || ticketData.TeamId.value === -1) {
      setFilteredUsers([ { ...defaultAssigneeOption }, ...usersInRole ]);
    } else {
      const filteredUsers = currentState.teams.filter(x => x.Id == ticketData.TeamId.value)[0]?.Users || [];
      setFilteredUsers([ { ...defaultAssigneeOption }, ...filteredUsers ]);
    }
  }

  const filterUsers = (value: any) => {
    defaultAssigneeOption.FullName = t('ticket.ticket-board.ticket-card.unassigned');
    if (value.Id === -1 || value.Id == null) {
      ifGroupNotSelected(value);
    } else {
      setFilteredUsers([ { ...defaultAssigneeOption }, ...value.Members ]);
    }
  }

  const groupHandler = (value: any, event: string) => {
    setTicketData(preState => ({ ...preState,
      SupportGroupId: { ...preState.SupportGroupId, value: value.Id },
      AssigneeId: { ...preState.AssigneeId, required: true, value: null }
    }));

    if (event == 'onChange') {
      filterUsers(value);
      forgetFilled(['AssigneeId']);
    }
  }

  const updateCustomPropertyData = (bulkData: BulkTicketEntity, cp: ICustomProperty, editData: BulkEditValue<string>) => {
    
    let updatedPropsAnswers = bulkData.CustomPropertiesAnswers ?? [];
    let editCustomProperty: BulkCustomPropertyEditValue = bulkData.CustomPropertiesAnswers?.find(c => c.key == cp.PropertyId);
    if(!editCustomProperty){
      editCustomProperty = { key: cp.PropertyId, editValue: editData } as BulkCustomPropertyEditValue;
      updatedPropsAnswers.push(editCustomProperty);
    }else
      updatedPropsAnswers = updatedPropsAnswers.map(p => {
        if(p.key == editCustomProperty.key)
          p.editValue = editData;
        return p;
      });
    
    setTicketData(preState => ({
      ...preState,
      CustomPropertiesAnswers: updatedPropsAnswers
    }));
  }

  const renderField = (globalTheme: any, propEditValue: BulkEditValue<string>, cp: ICustomProperty) => {
      let field = (
        <Input
          fluid
          disabled={!propEditValue.required}
          onChange={(_, p) => {
              propEditValue.value = p.value;
              updateCustomPropertyData(ticketData, cp, propEditValue);
          }}
          showSuccessIndicator={false}
        />
      ); 
      switch(CustomPropertyDataType_LABELS[cp.DataType]){
        case CustomPropertyDataType_LABELS.INT:
          field = (
            <Input 
              type="number"
              min={0}
              fluid
              disabled={!propEditValue.required}
              onChange={(_, p) => {
                propEditValue.value = p.value;
                updateCustomPropertyData(ticketData, cp, propEditValue);
              }}
              showSuccessIndicator={false}
            />
          );
          break;
        case CustomPropertyDataType_LABELS.TIME:
          field = <CustomTimePicker
                    onChange={p => {
                        const timeInput = (p.target as HTMLInputElement).value;
                        if(timeInput)
                            propEditValue.value = getCardTimeInputISOFormatted(timeInput);
                        else
                            propEditValue.value = timeInput;

                        updateCustomPropertyData(ticketData, cp, propEditValue);
                    }}
                    disabled={!propEditValue.required}
                  />
          break;
        case CustomPropertyDataType_LABELS.DATE:
          field = <>{TicketDatePicker(!propEditValue.required,
              propEditValue.value ? new Date(propEditValue.value): null,
              null,
              (_, p) => {
                  let selectedDate = p.value === null ? null : p.value?.toISOString();
                  propEditValue.value = selectedDate;
                  updateCustomPropertyData(ticketData, cp, propEditValue);
              },
              undefined,
              propEditValue.required ? () => {
                  propEditValue.value = null;
                  updateCustomPropertyData(ticketData, cp, propEditValue);
              } : undefined)}</>
          break;
        case CustomPropertyDataType_LABELS.BOOL:
          const boolOptions = ["False", "True"].map((c, i) => ({ header: c, accessibilityitemprops: i }));
          field = <TypeaheadItemPicker
            label={cp.Name}
            disabled={!propEditValue.required}
            items={boolOptions}
            valueId={boolOptionId(propEditValue.value)}
            onChange={(_item, id) => {
              propEditValue.value = _item ? boolOptionValue(id) : null;
              updateCustomPropertyData(ticketData, cp, propEditValue);
            }}
          />
          break;
        case CustomPropertyDataType_LABELS.LIST:
          const propertyListItems = (cp?.CustomPropertyListItems ?? [])
              .filter(pItem => !pItem.IsArchived && !pItem.IsDeleted)
              .map(item => ({
                  id: item.Id,
                  parentId: item.ParentId,
                  position: item.Position,
                  value: item.Value,
                  ...item
              } as Item));
          if(cp.CustomPropertyListConfiguration?.AllowMultiple)
          {
              field = <TypeaheadPicker
                  tabIndex={0}
                  ariaLabel={cp.Name}
                  disabled={!propEditValue.required}
                  items={propertyListItems}
                  defaultValue={propEditValue.value}
                  onChange={_ => {
                  }}
                  isMulti
                  onMultiChange={value => {
                    propEditValue.value = value?.join(",");
                    updateCustomPropertyData(ticketData, cp, propEditValue);
                  }}
              />
          }else{
              field = <TypeaheadPicker
                  tabIndex={0}
                  ariaLabel={cp.Name}
                  disabled={!propEditValue.required}
                  items={propertyListItems}
                  dataItem={{ id: propEditValue.value ? Number(propEditValue.value) : undefined } as Item}
                  defaultValue={null}
                  showClearBtn
                  onChange={value => {
                    propEditValue.value = value ? value.id.toString() : null;
                    updateCustomPropertyData(ticketData, cp, propEditValue);
                  }}
              />
          }
          break;
        default:
          break;
      }
      return field;
    }

  return (
    <FluentUIThemeConsumer
      render={globalTheme => {
        return (
          <div
            style={{
              height: '100%',
              ...ThemeColorScheme(globalTheme.siteVariables)
            }}
            >
            <div className={`${Styles.BulkEditTicketForm} CustomPropertiesContainer ${globalTheme.siteVariables.theme === themeNames.Dark ? 'cp_DarkContainer' : ''}`}>
              <LabelWithCheckbox
                checked={ticketData.RequesterId.required}
                label={t('ticket.add-ticket.requester')}
                onChange={() => {
                  setTicketData(preState => ({
                    ...preState,
                    RequesterId: {
                      ...preState.RequesterId,
                      required: !preState.RequesterId.required
                    }
                  }));
                }}
                error={emptyMessage('RequesterId')}
              >
                <div className={ticketData.RequesterId.required ? Styles.HoverablePeoplePicker : undefined}>
                  <PeoplePicker
                    userType={UserType.user} 
                    type={PersonType.person}
                    selectionMode="single"
                    selectionChanged={requestChangeHandler}
                    disabled={!ticketData.RequesterId.required}
                  />
                </div>
              </LabelWithCheckbox>

              <CheckFeature featureName={LICENSE_FEATURE.AffectedUsers}>
                <LabelWithCheckbox checked={ticketData.AffectedUsers.required}
                  label={t('ticket.ticket-list.affected-users')}
                  error={emptyMessage('AffectedUsers')}
                  onChange={() => {
                    setTicketData(preState => ({ ...preState, AffectedUsers: { ...preState.AffectedUsers, required: !preState.AffectedUsers.required } }));
                  }}>
                  <div className={ticketData.AffectedUsers.required ? Styles.HoverablePeoplePicker : undefined}>
                    <PeoplePicker
                      id='mgt-bulk-affected-users'
                      userType={UserType.user} 
                      type={PersonType.person}
                      className={`width-med height-small`}
                      disabled={!ticketData.AffectedUsers.required}
                      selectionChanged={affectedUserChangeHandler}
                    />
                  </div>
                </LabelWithCheckbox>
              </CheckFeature>

              <LabelWithCheckbox
                checked={ticketData.StatusId.required}
                label={t('ticket.add-ticket.status')}
                onChange={() => {
                  setTicketData(preState => ({
                    ...preState,
                    StatusId: {
                      ...preState.StatusId,
                      required: !preState.StatusId.required
                    }
                  }));
                }}
                error={emptyMessage('StatusId')}
              >
                <TypeaheadPicker
                  disabled={!ticketData.StatusId.required}
                  ariaLabel={t('ticket.add-ticket.status')}
                  items={enumItems(ticketStatus.filter(item => item.Id != getStatusIdByGuid(currentState.ticketStatus, STATUS_GUID.DEFLECTED)))}
                  dataItem={{ id: ticketData.StatusId.value ?? undefined } as Item}
                  defaultValue={null}
                  showClearBtn
                  onChange={value => {
                    setTicketData(preState => ({
                      ...preState,
                      StatusId: {
                        ...preState.StatusId,
                        value: value ? value.id : null
                      }
                    }));
                  }}
                />
              </LabelWithCheckbox>
              <CheckFeature featureName={LICENSE_FEATURE.TicketTypes}>
                <LabelWithCheckbox
                  checked={ticketData.TicketTypeId.required}
                  label={t('ticket.add-ticket.type')}
                  onChange={() => {
                    setTicketData(preState => ({
                      ...preState,
                      TicketTypeId: {
                        ...preState.TicketTypeId,
                        required: !preState.TicketTypeId.required
                      }
                    }));
                  }}
                  error={emptyMessage('TicketTypeId')}
                >
                  <TypeaheadPicker
                    disabled={!ticketData.TicketTypeId.required}
                    ariaLabel={t('ticket.add-ticket.type')}
                    items={enumItems(ticketTypes)}
                    dataItem={{ id: ticketData.TicketTypeId.value ?? undefined } as Item}
                    defaultValue={null}
                    showClearBtn
                    onChange={value => {
                      setTicketData(preState => ({
                        ...preState,
                        TicketTypeId: {
                          ...preState.TicketTypeId,
                          value: value ? value.id : null
                        }
                      }));
                    }}
                  />
                </LabelWithCheckbox>
              </CheckFeature>
              <LabelWithCheckbox
                checked={ticketData.CategoryId.required}
                label={t('ticket.add-ticket.category')}
                onChange={() => {
                  setTicketData(preState => ({
                    ...preState,
                    CategoryId: {
                      ...preState.CategoryId,
                      required: !preState.CategoryId.required
                    }
                  }));
                }}
                error={emptyMessage('CategoryId')}
              >
                <TypeaheadPicker
                  disabled={!ticketData.CategoryId.required}
                  ariaLabel={t('ticket.add-ticket.category')}
                  items={enumItems(categories)}
                  dataItem={{ id: ticketData.CategoryId.value ?? undefined } as Item}
                  defaultValue={null}
                  showClearBtn
                  onChange={value => {
                    setTicketData(preState => ({
                      ...preState,
                      CategoryId: {
                        ...preState.CategoryId,
                        value: value ? value.id : null
                      }
                    }));
                  }}
                />
              </LabelWithCheckbox>
              <LabelWithCheckbox
                checked={ticketData.PriorityId.required}
                label={t('ticket.add-ticket.priority')}
                onChange={() => {
                  setTicketData(preState => ({
                    ...preState,
                    PriorityId: {
                      ...preState.PriorityId,
                      required: !preState.PriorityId.required
                    }
                  }));
                }}
                error={emptyMessage('PriorityId')}
              >
                <TypeaheadPicker
                  disabled={!ticketData.PriorityId.required}
                  ariaLabel={t('ticket.add-ticket.priority')}
                  items={enumItems(priority)}
                  dataItem={{ id: ticketData.PriorityId.value ?? undefined } as Item}
                  defaultValue={null}
                  showClearBtn
                  onChange={value => {
                    setTicketData(preState => ({
                      ...preState,
                      PriorityId: {
                        ...preState.PriorityId,
                        value: value ? value.id : null
                      }
                    }));
                  }}
                />
              </LabelWithCheckbox>

              <LabelWithCheckbox
                checked={assignmentCheckbox(enabledMultiDepartment, ticketData)}
                label={t('ticket.add-ticket.assignment')}
                onChange={() => {
                  setTicketData(preState => ({
                    ...preState,
                    TeamId: {
                      ...preState.TeamId,
                      required: enabledMultiDepartment && !preState.TeamId.required
                    },
                    SupportGroupId: {
                      ...preState.SupportGroupId,
                      required: !preState.SupportGroupId.required
                    },
                    AssigneeId: {
                      ...preState.AssigneeId,
                      required: !preState.AssigneeId.required
                    }
                  }));
                }}
              >
                <div>
                  <CheckFeature featureName={LICENSE_FEATURE.MultiDepartment}>
                    <LabelElement label={t('ticket.add-ticket.team')}>
                    <TypeaheadItemPicker
                      label={t('ticket.add-ticket.team')}
                      disabled={!ticketData.TeamId.required}
                      items={pickerItems(teamsBulkEdit, 'FriendlyName')}
                      valueId={ticketData.TeamId.value}
                      clearable
                      onChange={(_item, id) => handlerTeam(teamsBulkEdit.find(x => x.Id === id) ?? { Id: null }, 'onChange')}
                    />
                    {emptyMessage('TeamId') && <FieldErrorText>{emptyMessage('TeamId')}</FieldErrorText>}
                    </LabelElement>
                  </CheckFeature>
                  <div className={`pt-2`}>
                    <Text content={t('ticket.add-ticket.group')}></Text>
                    <TypeaheadItemPicker
                      label={t('ticket.add-ticket.group')}
                      disabled={!ticketData.SupportGroupId.required}
                      items={pickerItems(supportGroupsBulkEdit, 'Name')}
                      valueId={ticketData.SupportGroupId.value}
                      clearable
                      onChange={(_item, id) => groupHandler(supportGroupsBulkEdit.find(x => x.Id === id) ?? { Id: null }, 'onChange')}
                    />
                    {emptyMessage('SupportGroupId') && <FieldErrorText>{emptyMessage('SupportGroupId')}</FieldErrorText>}
                  </div>
                  <div className={`pt-2`}>
                    <Text content={t('ticket.add-ticket.assignee')}></Text>
                    <TypeaheadItemPicker
                      label={t('ticket.add-ticket.assignee')}
                      disabled={!ticketData.AssigneeId.required}
                      items={pickerItems(filteredUsers, 'FullName')}
                      valueId={ticketData.AssigneeId.value || null}
                      valueName={usersInRole.find(x => x.Id == ticketData.AssigneeId.value)?.FullName}
                      clearable
                      onChange={(_item, id) => setTicketData(preState => ({ ...preState, AssigneeId: { ...preState.AssigneeId, value: id || 0 } }))}
                    />
                    {emptyMessage('AssigneeId') && <FieldErrorText>{emptyMessage('AssigneeId')}</FieldErrorText>}
                  </div>
                </div>
              </LabelWithCheckbox>

              <CheckFeature featureName={LICENSE_FEATURE.Collaborators}>
                <LabelWithCheckbox checked={ticketData.TicketCollaborators.required}
                  label={t('ticket.ticket-list.collaborators')}
                  error={emptyMessage('TicketCollaborators')}
                  onChange={() => {
                    setTicketData(preState => ({ ...preState, TicketCollaborators: { ...preState.TicketCollaborators, required: !preState.TicketCollaborators.required } }));
                  }}>
                  <MultiTypeaheadField
                    label={t('ticket.ticket-list.collaborators')}
                    hideLabel
                    listFitsBox
                    placeholder={t('ticket.add-ticket.collaborators')}
                    disabled={!ticketData.TicketCollaborators.required}
                    options={collaboratorOptions}
                    valueIds={(ticketData.TicketCollaborators.value ?? []).filter(id => collaboratorOptions.some(option => option.id === id))}
                    onChange={collaboratorChangeHandler}
                  />
                </LabelWithCheckbox>
              </CheckFeature>

              <LabelWithCheckbox
                checked={ticketData.DueDate.required}
                label={t('ticket.add-ticket.due-date')}
                onChange={() => {
                  setTicketData(preState => ({
                    ...preState,
                    DueDate: {
                      ...preState.DueDate,
                      required: !preState.DueDate.required
                    }
                  }));
                }}
                error={emptyMessage('DueDate')}
              >
                {TicketDatePicker(!ticketData.DueDate.required,
                  ticketData.DueDate.value ? new Date(ticketData.DueDate.value) : null,
                  null,
                  (_e, p) => setTicketData(preState => ({ ...preState, DueDate: { ...preState.DueDate, value: new Date(p.value) } })),
                  undefined,
                  () => setTicketData(preState => ({ ...preState, DueDate: { ...preState.DueDate, value: null } })))}
              </LabelWithCheckbox>

              <LabelWithCheckbox label={t('ticket.add-ticket.resolution-date')}
                checked={ticketData.ResolutionDate.required}
                error={emptyMessage('ResolutionDate')}
                onChange={() => {
                  setTicketData(preState => ({
                    ...preState, ResolutionDate: { ...preState.ResolutionDate, required: !preState.ResolutionDate.required }
                  }));
                }}
              >
                {TicketDatePicker(!ticketData.ResolutionDate.required,
                  ticketData.ResolutionDate.value ? new Date(ticketData.ResolutionDate.value) : null,
                  null,
                  (_e, p) => setTicketData(preState => ({ ...preState, ResolutionDate: { ...preState.ResolutionDate, value: new Date(p.value) } })),
                  undefined,
                  () => setTicketData(preState => ({ ...preState, ResolutionDate: { ...preState.ResolutionDate, value: null } })))}
              </LabelWithCheckbox>
              <CheckFeature featureName={LICENSE_FEATURE.TicketTags}>
                <LabelWithCheckbox
                  checked={ticketData.Tags.required}
                  label={t('ticket.add-ticket.tag')}
                  onChange={() => {
                    setTicketData(preState => ({
                      ...preState,
                      Tags: {
                        ...preState.Tags,
                        required: !preState.Tags.required
                      }
                    }));
                  }}
                  error={emptyMessage('Tags')}
                >
                  <MultiTypeaheadField
                    label={t('ticket.add-ticket.tag')}
                    hideLabel
                    listFitsBox
                    placeholder={TAGS.SELECT_TAGS}
                    disabled={!ticketData.Tags.required}
                    options={tagOptions}
                    valueIds={(ticketData.Tags.value ?? []).filter(id => tagOptions.some(option => option.id === id))}
                    onChange={ids => setTicketData(preState => ({ ...preState, Tags: { ...preState.Tags, value: ids } }))}
                    onCreate={createTagFromBox}
                    createLabel={name => `${t('ticket.add-ticket.tags.new-tag')} '${name}'`}
                    sortChips
                  />
                </LabelWithCheckbox>
              </CheckFeature>

              <CheckFeature featureName={LICENSE_FEATURE.CustomProperties}>
                {currentState?.customProperties.filter(p => !p.IsArchived && !p.IsHidden).map(cp => {
                  const propData = ticketData.CustomPropertiesAnswers?.find(c => c.key == cp.PropertyId);
                  const propEditValue = propData?.editValue ?? {value: null, required: false} as BulkEditValue<string>;
                  
                  return <>
                    <LabelWithCheckbox
                      checked={propEditValue.required}
                      label={cp.Name}
                      onChange={() => {
                        propEditValue.required = !propEditValue.required;
                        updateCustomPropertyData(ticketData, cp, propEditValue);
                      }}
                      error={emptyMessage(customPropertyKey(cp.PropertyId))}
                    >
                    {renderField(globalTheme, propEditValue, cp)}
                    </LabelWithCheckbox>
                  </>
                })}
              </CheckFeature>
            </div>
          </div>
        );
      }}
    />
  );
};
